import React, { useState } from "react";
import { api } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Loader2 } from "lucide-react";
import { toast } from "sonner";
import { FINANCE_TYPES, INDUSTRY_SECTORS } from "@/pages/hub/constants";
import { MoneyField, CountField, PercentField } from "@/components/ui/number-fields";

/** Lender self-serve criteria tab — matches the /hub aesthetic, saves to /api/lender/profile */
export function LenderCriteriaForm({ me, onSaved }) {
  const [form, setForm] = useState({
    contact_name: me?.contact_name || "",
    phone: me?.phone || "",
    website: me?.website || "",
    address: me?.address || "",
    finance_types:    me?.finance_types    || [],
    industry_sectors: me?.industry_sectors || [],
    min_deal_size_gbp: me?.min_deal_size_gbp ?? "",
    max_deal_size_gbp: me?.max_deal_size_gbp ?? "",
    turnaround_days:   me?.turnaround_days ?? "",
    lending_criteria:  me?.lending_criteria || "",
    expected_fee_pct:  me?.expected_fee_pct ?? "",
  });
  const [busy, setBusy] = useState(false);

  const setField = (k, v) => setForm((f) => ({ ...f, [k]: v }));
  const toggleIn = (key, val) => setForm((f) => ({ ...f, [key]: f[key].includes(val) ? f[key].filter((x) => x !== val) : [...f[key], val] }));

  const save = async () => {
    setBusy(true);
    try {
      const payload = {
        contact_name: form.contact_name, phone: form.phone, website: form.website, address: form.address,
        finance_types: form.finance_types, industry_sectors: form.industry_sectors,
        min_deal_size_gbp: parseFloat(form.min_deal_size_gbp) || 0,
        max_deal_size_gbp: parseFloat(form.max_deal_size_gbp) || 0,
        turnaround_days: form.turnaround_days === "" ? null : parseInt(form.turnaround_days, 10),
        lending_criteria: form.lending_criteria,
        expected_fee_pct: form.expected_fee_pct === "" ? null : parseFloat(form.expected_fee_pct),
      };
      const r = await api.patch("/lender/profile", payload);
      toast.success("Your lender profile has been updated");
      onSaved?.(r.data);
    } catch (e) { toast.error(e?.response?.data?.detail || "Save failed"); }
    finally { setBusy(false); }
  };

  return (
    <div className="space-y-4" data-testid="lender-criteria">
      <div className="rounded-2xl border border-slate-200 bg-white p-5">
        <div className="text-[10.5px] uppercase tracking-[0.22em] font-semibold text-slate-600">Company</div>
        <div className="mt-4 grid sm:grid-cols-2 gap-3">
          <F label="Contact name"><Input value={form.contact_name} onChange={(e) => setField("contact_name", e.target.value)} data-testid="lender-contact-name" /></F>
          <F label="Phone"><Input value={form.phone} onChange={(e) => setField("phone", e.target.value)} /></F>
          <F label="Website"><Input value={form.website} onChange={(e) => setField("website", e.target.value)} /></F>
          <F label="Address"><Input value={form.address} onChange={(e) => setField("address", e.target.value)} /></F>
        </div>
      </div>

      <div className="rounded-2xl border border-slate-200 bg-white p-5">
        <div className="text-[10.5px] uppercase tracking-[0.22em] font-semibold text-slate-600">Lending appetite</div>
        <p className="mt-1 text-[12.5px] text-slate-500">These filters gate which leads Connexis routes to your desk.</p>

        <div className="mt-4">
          <Label className="text-[10.5px] uppercase tracking-[0.14em] text-slate-600 font-semibold">Finance types</Label>
          <div className="mt-2 flex flex-wrap gap-1.5">
            {FINANCE_TYPES.map((t) => (
              <button key={t} type="button" onClick={() => toggleIn("finance_types", t)}
                      className={`inline-flex items-center rounded-full px-2.5 h-7 text-[11.5px] font-medium border transition-colors ${form.finance_types.includes(t) ? "bg-[#0055FF] border-[#0055FF] text-white" : "bg-white border-slate-300 text-slate-700 hover:border-slate-400"}`}
                      data-testid={`lender-ft-${t}`}>{t}</button>
            ))}
          </div>
        </div>

        <div className="mt-5">
          <Label className="text-[10.5px] uppercase tracking-[0.14em] text-slate-600 font-semibold">Industry sectors</Label>
          <div className="mt-2 flex flex-wrap gap-1.5 max-h-44 overflow-y-auto pr-1">
            {INDUSTRY_SECTORS.map((s) => (
              <button key={s} type="button" onClick={() => toggleIn("industry_sectors", s)}
                      className={`inline-flex items-center rounded-full px-2.5 h-7 text-[11.5px] font-medium border transition-colors ${form.industry_sectors.includes(s) ? "bg-[#0055FF] border-[#0055FF] text-white" : "bg-white border-slate-300 text-slate-700 hover:border-slate-400"}`}>{s}</button>
            ))}
          </div>
        </div>

        <div className="mt-5 grid sm:grid-cols-3 gap-3">
          <F label="Min deal size"><MoneyField value={form.min_deal_size_gbp} onChange={(v) => setField("min_deal_size_gbp", v)} compact testId="lender-min-deal" hint="drag or type" /></F>
          <F label="Max deal size"><MoneyField value={form.max_deal_size_gbp} onChange={(v) => setField("max_deal_size_gbp", v)} compact testId="lender-max-deal" hint="0 = unlimited" /></F>
          <F label="Typical turnaround"><CountField value={form.turnaround_days} onChange={(v) => setField("turnaround_days", v)} max={90} suffix="days" testId="lender-turnaround" /></F>
        </div>

        <div className="mt-5" data-testid="lender-fee-block">
          <Label className="text-[10.5px] uppercase tracking-[0.14em] text-slate-600 font-semibold">Your typical fee on a funded deal</Label>
          <p className="mt-1 mb-2 text-[12.5px] text-slate-500">
            What your desk earns, as a % of the facility — not what you pay us. It sizes every case before it funds and
            updates the Connexis desk the moment you save. The real figure is still confirmed per deal.
          </p>
          <PercentField value={form.expected_fee_pct} onChange={(v) => setField("expected_fee_pct", v)} max={20} step={0.25}
                        presets={[1, 1.5, 2, 2.5, 3, 5, 10, 20]} testId="lender-expected-fee"
                        hint={me?.expected_fee_source === "desk" ? "your figure — change it whenever your pricing moves" : me?.expected_fee_pct != null ? "set by the Connexis desk as a forecast — correct it here if it's wrong" : "nothing on file yet — a blank is read as 2.5%"} />
        </div>

        <div className="mt-5">
          <Label className="text-[10.5px] uppercase tracking-[0.14em] text-slate-600 font-semibold">Detailed lending criteria</Label>
          <Textarea value={form.lending_criteria} onChange={(e) => setField("lending_criteria", e.target.value)} className="mt-1.5 min-h-[140px]" placeholder="Free-text — deal structures, geography, LTV limits, sectors to avoid, etc." data-testid="lender-criteria-notes" />
        </div>

        <div className="mt-6 flex items-center justify-end">
          <Button onClick={save} disabled={busy} className="bg-[#0055FF] hover:bg-[#0044CC]" data-testid="lender-save-profile">
            {busy ? <Loader2 className="size-4 animate-spin" /> : "Save changes"}
          </Button>
        </div>
      </div>
    </div>
  );
}


function F({ label, children }) {
  return (
    <div>
      <Label className="text-[10.5px] uppercase tracking-[0.14em] text-slate-600 font-semibold">{label}</Label>
      <div className="mt-1.5">{children}</div>
    </div>
  );
}
